import { Params } from '@angular/router';
import { getUrl, RouteDef } from '@cumlaude/shared-components-menu';
import { routeNaam, UserRouteData } from '../../../services/user-routes.service';

/**
 * Eén label+url-paar uit het static path van een pagina, vóór context (klikbaarheid, huidige
 * pagina) is toegevoegd. Puur afgeleid uit de routeboom.
 */
export interface StaticCrumb {
	readonly label: string;
	readonly url: string;
}

/**
 * Eén zichtbare crumb in de breadcrumb. `url`/`queryParams` zijn alleen gezet als de crumb
 * klikbaar is -- een groep-crumb heeft geen navigatiedoel nodig.
 */
export interface Crumb {
	readonly label: string;
	readonly url?: string;
	readonly queryParams?: Params;
	readonly clickable: boolean;
	readonly current: boolean;
}

/**
 * Zoekt de RouteDef die exact op `path` uitkomt, door de (rechten-gefilterde) routeboom te
 * doorlopen. Vergelijkt op de volledige url van elke node (getUrl(elements)), niet op een los
 * padsegment -- zo hoeft de aanroeper niet zelf de boom segment voor segment af te lopen.
 */
export function findRouteDef(routes: readonly RouteDef<UserRouteData>[], path: string): RouteDef<UserRouteData> | undefined {
	for (const route of routes) {
		if (route.elements && getUrl({ routeElements: route.elements }) === path) return route;
		if (route.subRoutes) {
			const gevonden = findRouteDef(route.subRoutes, path);
			if (gevonden) return gevonden;
		}
	}
	return undefined;
}

/**
 * Het static path van `path`: het label + de url van elke voorouder in de routeboom, van het
 * hoogste niveau tot en met `path` zelf. Stopt bij een `eigenTabbalk`-node (bv. Details > Leerling
 * i.p.v. Details > Leerling > Doorstroom -- de subpagina's daaronder horen bij de eigen tabbalk,
 * niet bij de breadcrumb). Opeenvolgende crumbs met hetzelfde label (bv. Cohortrendement als
 * tussenknoop en als bladknoop) worden gededupliceerd tot de diepste van de reeks.
 */
export function staticPath(routes: readonly RouteDef<UserRouteData>[], path: string): StaticCrumb[] {
	const node = findRouteDef(routes, path);
	if (!node?.elements) return [];

	const eigenTabbalkIndex = node.elements.findIndex((element) => element.routeData!.eigenTabbalk);
	const elements = eigenTabbalkIndex === -1 ? node.elements : node.elements.slice(0, eigenTabbalkIndex + 1);

	const crumbs = elements.map((element, i) => ({
		label: routeNaam(element.routeData!),
		url: getUrl({ routeElements: elements.slice(0, i + 1) }),
	}));

	return dedupeAdjacentLabels(crumbs);
}

/**
 * Houdt van elke reeks opeenvolgende crumbs met hetzelfde label alleen de laatste (= diepste) over.
 */
function dedupeAdjacentLabels(crumbs: readonly StaticCrumb[]): StaticCrumb[] {
	return crumbs.filter((crumb, i) => i === crumbs.length - 1 || crumb.label !== crumbs[i + 1].label);
}

/**
 * De sleutel waarop twee urls "dezelfde pagina" zijn voor de breadcrumb-trail: `path` zelf, tenzij
 * `path` binnen een `eigenTabbalk`-node valt (bv. Details > Leerling), in welk geval alle tabs
 * daaronder (Doorstroom, Cijferlijst, ...) dezelfde sleutel krijgen -- zonder deze truncation zou
 * een tabwissel binnen zo'n detailpagina de trail zichzelf laten aanvullen.
 */
export function crumbPath(routes: readonly RouteDef<UserRouteData>[], path: string): string {
	const elements = findRouteDef(routes, path)?.elements;
	if (!elements) return path;

	const eigenTabbalkIndex = elements.findIndex((element) => element.routeData!.eigenTabbalk);
	if (eigenTabbalkIndex === -1) return path;

	return getUrl({ routeElements: elements.slice(0, eigenTabbalkIndex + 1) });
}

/**
 * Alleen een doelpad onder `details/` mag de breadcrumb-trail verlengen -- dat is in deze codebase
 * precies de verzameling niet-via-het-menu-bereikbare doorklikpagina's (zie de `from`/`fixedWeergaveOpties`
 * op de details-bladeren in de dashboard-registry).
 */
export function isDetailsPath(path: string): boolean {
	return path.startsWith('/details/');
}

/**
 * Vanaf dit aantal crumbs wordt altijd minimaal de eerste getruncate, ook als de rij toevallig
 * past. Komt overeen met de Figma-voorbeelden: 5 crumbs -> 1 getruncate, 7 crumbs -> 2 getruncate.
 */
export const MIN_CRUMBS_VOOR_TRUNCATION = 5;

/**
 * Bepaalt welke crumb-indices getruncate worden tot "…": vanaf MIN_CRUMBS_VOOR_TRUNCATION crumbs
 * wordt altijd minimaal de eerste getruncate; daarna wordt van links naar rechts verder getruncate
 * zolang de rij niet in `beschikbaar` past. De laatste crumb wordt nooit getruncate -- past hij
 * alsnog niet, dan ellipsizet hij via CSS (text-overflow) in plaats van via "…".
 *
 * `volledigeBreedtes` is de breedte van elke crumb in zijn volledige vorm (incl. chevron en
 * interne gap), in dezelfde volgorde als de crumbs. `getruncateBreedte` is de breedte van een
 * getruncate crumb ("…" i.p.v. het label) -- gelijk voor elke crumb, dus één getal. `beschikbaar`
 * is de resterende breedte voor de crumbs-rij zelf, dus ná aftrek van het huisje en de
 * tussenruimtes (die zijn vast en truncaten nooit mee).
 */
export function truncate(volledigeBreedtes: readonly number[], getruncateBreedte: number, beschikbaar: number): ReadonlySet<number> {
	const aantal = volledigeBreedtes.length;
	if (aantal < MIN_CRUMBS_VOOR_TRUNCATION) return new Set();

	const getruncateIndices = new Set<number>([0]);
	const totaleBreedte = () => volledigeBreedtes.reduce((som, breedte, i) => som + (getruncateIndices.has(i) ? getruncateBreedte : breedte), 0);

	let i = 1;
	while (totaleBreedte() > beschikbaar && i < aantal - 1) {
		getruncateIndices.add(i);
		i++;
	}

	return getruncateIndices;
}
